import { Injectable } from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import {
  MdbNotificationService,
  MdbNotificationRef,
} from 'mdb-angular-ui-kit/notification';
import { SuccessToastComponent } from './success-toast.component';
import { ErrorToastComponent } from './error-toast.component';
import { WarningToastComponent } from './warning-toast.component';
import { InfoToastComponent } from './info-toast.component';

@Injectable({
  providedIn: 'root',
})
export class MdbToastService {
  constructor(
    private notificationService: MdbNotificationService,
    private liveAnnouncer: LiveAnnouncer
  ) {}

  /**
   * Toasts are inserted into the DOM together with their text, which screen readers do not
   * announce. The CDK announcer keeps one live region in the page for the whole app session.
   */
  private announce(message: string, politeness: 'polite' | 'assertive'): void {
    if (message) void this.liveAnnouncer.announce(message, politeness);
  }

  /**
   * Gets the appropriate offset based on current context
   * Game module has no header, so needs smaller offset
   */
  private getOffset(): number {
    const isInGame = document.body.classList.contains('game-shell-active');
    return isInGame ? 20 : 80;
  }

  /**
   * Gets responsive configuration for mobile devices
   */
  private getMobileConfig() {
    const isMobile = window.innerWidth <= 480;
    return {
      position: isMobile ? 'top-right' : 'top-right',
      offset: isMobile ? 8 : this.getOffset()
    };
  }

  /**
   * Shows a success toast message
   */
  success(
    message: string,
    title?: string
  ): MdbNotificationRef<SuccessToastComponent> {
    const config = this.getMobileConfig();
    this.announce(message, 'polite');
    const ref = this.notificationService.open(SuccessToastComponent, {
      data: { message },
      position: config.position as any,
      stacking: true,
      autohide: true,
      delay: 5000,
      animation: true,
      offset: config.offset,
    });
    return ref;
  }

  /**
   * Shows an error toast message
   */
  error(
    message: string,
    title?: string
  ): MdbNotificationRef<ErrorToastComponent> {
    const config = this.getMobileConfig();
    this.announce(message, 'assertive');
    const ref = this.notificationService.open(ErrorToastComponent, {
      data: { message },
      position: config.position as any,
      stacking: true,
      autohide: true,
      delay: 8000, // Errors stay longer
      animation: true,
      offset: config.offset,
    });
    return ref;
  }

  /**
   * Shows a warning toast message
   */
  warning(
    message: string,
    title?: string
  ): MdbNotificationRef<WarningToastComponent> {
    const config = this.getMobileConfig();
    this.announce(message, 'polite');
    const ref = this.notificationService.open(WarningToastComponent, {
      data: { message },
      position: config.position as any,
      stacking: true,
      autohide: true,
      delay: 6000,
      animation: true,
      offset: config.offset,
    });
    return ref;
  }

  /**
   * Shows an info toast message
   */
  info(
    message: string,
    title?: string
  ): MdbNotificationRef<InfoToastComponent> {
    const config = this.getMobileConfig();
    this.announce(message, 'polite');
    const ref = this.notificationService.open(InfoToastComponent, {
      data: { message },
      position: config.position as any,
      stacking: true,
      autohide: true,
      delay: 5000,
      animation: true,
      offset: config.offset,
    });
    return ref;
  }

  /**
   * Shows a toast with custom configuration
   */
  show(
    message: string,
    type: 'success' | 'error' | 'warning' | 'info' = 'info',
    title?: string,
    config?: any
  ): MdbNotificationRef<any> {
    switch (type) {
      case 'success':
        return this.success(message, title);
      case 'error':
        return this.error(message, title);
      case 'warning':
        return this.warning(message, title);
      case 'info':
      default:
        return this.info(message, title);
    }
  }
}
